// Modified splaTV hybrid.js as a module
// Original by antimatter15: https://github.com/antimatter15/splaTV

import { GizmoRenderer } from './gizmo-renderer.js';

const vertexShaderSource = `#version 300 es
precision highp float;
precision highp int;

uniform highp usampler2D u_texture;
uniform mat4 projection, view;
uniform mat4 modelMatrix;
uniform vec2 focal;
uniform vec2 viewport;
uniform float time;

in vec2 position;
in int index;

out vec4 vColor;
out vec2 vPosition;

void main () {
    gl_Position = vec4(0.0, 0.0, 2.0, 1.0);

    uvec4 motion1 = texelFetch(u_texture, ivec2(((uint(index) & 0x3ffu) << 2) | 3u, uint(index) >> 10), 0);
    vec2 trbf = unpackHalf2x16(motion1.w);
    float dt = time - trbf.x;

    float topacity = exp(-1.0 * pow(dt / trbf.y, 2.0));
    if(topacity < 0.02) return;

    uvec4 motion0 = texelFetch(u_texture, ivec2(((uint(index) & 0x3ffu) << 2) | 2u, uint(index) >> 10), 0);
    uvec4 static0 = texelFetch(u_texture, ivec2(((uint(index) & 0x3ffu) << 2), uint(index) >> 10), 0);

    vec2 m0 = unpackHalf2x16(motion0.x), m1 = unpackHalf2x16(motion0.y), m2 = unpackHalf2x16(motion0.z), 
         m3 = unpackHalf2x16(motion0.w), m4 = unpackHalf2x16(motion1.x); 
    
    vec4 trot = vec4(unpackHalf2x16(motion1.y).xy, unpackHalf2x16(motion1.z).xy) * dt;
    vec3 tpos = (vec3(m0.xy, m1.x) * dt + vec3(m1.y, m2.xy) * dt*dt + vec3(m3.xy, m4.x) * dt*dt*dt);
    
    vec4 worldPos = modelMatrix * vec4(uintBitsToFloat(static0.xyz) + tpos, 1);
    vec4 cam = view * worldPos;
    vec4 pos = projection * cam;

    float clip = 1.2 * pos.w;
    if (pos.z < -clip || pos.x < -clip || pos.x > clip || pos.y < -clip || pos.y > clip) return;
    uvec4 static1 = texelFetch(u_texture, ivec2(((uint(index) & 0x3ffu) << 2) | 1u, uint(index) >> 10), 0);

    vec4 rot = vec4(unpackHalf2x16(static0.w).xy, unpackHalf2x16(static1.x).xy) + trot;
    vec3 scale = vec3(unpackHalf2x16(static1.y).xy, unpackHalf2x16(static1.z).x);
    rot /= sqrt(dot(rot, rot));

    mat3 S = mat3(scale.x, 0.0, 0.0, 0.0, scale.y, 0.0, 0.0, 0.0, scale.z);
    mat3 R = mat3(
      1.0 - 2.0 * (rot.z * rot.z + rot.w * rot.w), 2.0 * (rot.y * rot.z - rot.x * rot.w), 2.0 * (rot.y * rot.w + rot.x * rot.z),
      2.0 * (rot.y * rot.z + rot.x * rot.w), 1.0 - 2.0 * (rot.y * rot.y + rot.w * rot.w), 2.0 * (rot.z * rot.w - rot.x * rot.y),
      2.0 * (rot.y * rot.w - rot.x * rot.z), 2.0 * (rot.z * rot.w + rot.x * rot.y), 1.0 - 2.0 * (rot.y * rot.y + rot.z * rot.z));
    mat3 M = S * R;
    mat3 Vrk = 4.0 * transpose(M) * M;
    mat3 J = mat3(
        focal.x / cam.z, 0., -(focal.x * cam.x) / (cam.z * cam.z), 
        0., -focal.y / cam.z, (focal.y * cam.y) / (cam.z * cam.z), 
        0., 0., 0.
    );

    mat3 T = transpose(mat3(view)) * J;
    mat3 cov2d = transpose(T) * Vrk * T;

    float mid = (cov2d[0][0] + cov2d[1][1]) / 2.0;
    float radius = length(vec2((cov2d[0][0] - cov2d[1][1]) / 2.0, cov2d[0][1]));
    float lambda1 = mid + radius, lambda2 = mid - radius;

    if(lambda2 < 0.0) return;
    vec2 diagonalVector = normalize(vec2(cov2d[0][1], lambda1 - cov2d[0][0]));
    vec2 majorAxis = min(sqrt(2.0 * lambda1), 1024.0) * diagonalVector;
    vec2 minorAxis = min(sqrt(2.0 * lambda2), 1024.0) * vec2(diagonalVector.y, -diagonalVector.x);
    
    uint rgba = static1.w;
    vColor = 
      clamp(pos.z/pos.w+1.0, 0.0, 1.0) * 
      vec4(1.0, 1.0, 1.0, topacity) *
      vec4(
        (rgba) & 0xffu, 
        (rgba >> 8) & 0xffu, 
        (rgba >> 16) & 0xffu, 
        (rgba >> 24) & 0xffu) / 255.0;

    vec2 vCenter = vec2(pos) / pos.w;
    gl_Position = vec4(
        vCenter 
        + position.x * majorAxis / viewport 
        + position.y * minorAxis / viewport, 0.0, 1.0);

    vPosition = position;
}
`;

const fragmentShaderSource = `#version 300 es
precision highp float;

in vec4 vColor;
in vec2 vPosition;

out vec4 fragColor;

void main () {
    float A = -dot(vPosition, vPosition);
    if (A < -4.0) discard;
    float B = exp(A) * vColor.a;
    fragColor = vec4(B * vColor.rgb, B);
}
`;

// Sorting worker code
const sortWorkerCode = `
let vertexCount = 0;
let depthIndex = new Uint32Array();
let positions;
let transformedPositions;
let lastProj = [];
let lastVertexCount = 0;
let lastModelMatrix = null;
let modelMatrix = new Float32Array(16);

// Initialize model matrix to identity
modelMatrix.set([1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1]);

function transformPositions() {
    if (!positions) return;
    
    if (!transformedPositions || transformedPositions.length !== positions.length) {
        transformedPositions = new Float32Array(positions.length);
    }
    
    // Apply model matrix to all positions
    for (let i = 0; i < vertexCount; i++) {
        const x = positions[i * 3];
        const y = positions[i * 3 + 1];
        const z = positions[i * 3 + 2];
        
        // Transform position by model matrix
        transformedPositions[i * 3] = modelMatrix[0] * x + modelMatrix[4] * y + modelMatrix[8] * z + modelMatrix[12];
        transformedPositions[i * 3 + 1] = modelMatrix[1] * x + modelMatrix[5] * y + modelMatrix[9] * z + modelMatrix[13];
        transformedPositions[i * 3 + 2] = modelMatrix[2] * x + modelMatrix[6] * y + modelMatrix[10] * z + modelMatrix[14];
    }
}

function runSort(viewProj) {
    if (!positions) return;
    
    if (lastVertexCount == vertexCount) {
        let dist = Math.hypot(...[2, 6, 10].map((k) => lastProj[k] - viewProj[k]));
        if (dist < 0.01) return;
    } else {
        lastVertexCount = vertexCount;
    }

    // Use transformed positions for depth calculation
    const positionsToSort = transformedPositions || positions;
    
    let maxDepth = -Infinity;
    let minDepth = Infinity;
    let sizeList = new Int32Array(vertexCount);
    
    for (let i = 0; i < vertexCount; i++) {
        let depth = ((viewProj[2] * positionsToSort[3 * i + 0] + 
                      viewProj[6] * positionsToSort[3 * i + 1] + 
                      viewProj[10] * positionsToSort[3 * i + 2]) * 4096) | 0;
        sizeList[i] = depth;
        if (depth > maxDepth) maxDepth = depth;
        if (depth < minDepth) minDepth = depth;
    }

    // 16 bit single-pass counting sort
    let depthInv = (256 * 256) / (maxDepth - minDepth);
    let counts0 = new Uint32Array(256 * 256);
    for (let i = 0; i < vertexCount; i++) {
        sizeList[i] = ((sizeList[i] - minDepth) * depthInv) | 0;
        counts0[sizeList[i]]++;
    }
    
    let starts0 = new Uint32Array(256 * 256);
    for (let i = 1; i < 256 * 256; i++) {
        starts0[i] = starts0[i - 1] + counts0[i - 1];
    }
    
    depthIndex = new Uint32Array(vertexCount);
    for (let i = 0; i < vertexCount; i++) {
        depthIndex[starts0[sizeList[i]]++] = i;
    }

    lastProj = viewProj;
    self.postMessage({ depthIndex, viewProj, vertexCount }, [depthIndex.buffer]);
}

self.onmessage = (e) => {
    if (e.data.positions) {
        positions = e.data.positions;
        vertexCount = e.data.vertexCount;
        // Re-transform positions with current model matrix
        transformPositions();
    }
    if (e.data.modelMatrix) {
        // Update model matrix and re-transform positions
        modelMatrix.set(e.data.modelMatrix);
        transformPositions();
        // Force re-sort by invalidating last projection
        lastProj = [];
    }
    if (e.data.viewProj) {
        runSort(e.data.viewProj);
    }
};
`;

export class SplatViewer {
    constructor(canvas) {
        this.canvas = canvas;
        this.gl = canvas.getContext('webgl2', {
            antialias: false,
            alpha: true,
            preserveDrawingBuffer: true, // Change to true for debugging
            premultipliedAlpha: false,
            xrCompatible: true // Enable XR compatibility
        });

        if (!this.gl) {
            throw new Error('WebGL2 not supported');
        }

        // Verify WebGL2 context
        const version = this.gl.getParameter(this.gl.VERSION);
        if (!version.includes('WebGL 2')) {
            console.warn('WebGL 2 context requested but got:', version);
        }

        this.vertexCount = 0;
        this.splatCount = 0;
        this.splatData = null;
        this.texture = null;
        this.program = null;
        this.vao = null;
        this.initialized = false;
        this.worker = null;
        this.depthIndex = null;
        this.viewMatrix = new Float32Array(16);
        this.projMatrix = new Float32Array(16);
        this.modelMatrix = new Float32Array(16);
        this.lastSortTime = 0;
        this.bounds = null;
        
        // Focal length values
        this.fx = 0;
        this.fy = 0;
        
        // Gizmo renderer
        this.gizmoRenderer = null;
        this.showGizmo = false;
        this.gizmoMode = 'translate'; // 'translate', 'rotate', 'scale'
        
        
        // Initialize model matrix to identity
        this.modelMatrix.set([
            1, 0, 0, 0,
            0, 1, 0, 0,
            0, 0, 1, 0,
            0, 0, 0, 1
        ]);

        // Set default view matrix from antimatter15's implementation
        const defaultViewMatrix = [0.99, 0.01, -0.14, 0, 0.02, 0.99, 0.12, 0, 0.14, -0.12, 0.98, 0, -0.09, -0.26, 0.2, 1];
        this.viewMatrix.set(defaultViewMatrix);

        this.init();
        
        // Initialize projection matrix with default FOV
        this.updateProjectionMatrix(45);
    }

    init() {
        const gl = this.gl;

        // Create shader program
        const vertexShader = this.createShader(gl.VERTEX_SHADER, vertexShaderSource);
        const fragmentShader = this.createShader(gl.FRAGMENT_SHADER, fragmentShaderSource);
        
        this.program = gl.createProgram();
        gl.attachShader(this.program, vertexShader);
        gl.attachShader(this.program, fragmentShader);
        gl.linkProgram(this.program);

        if (!gl.getProgramParameter(this.program, gl.LINK_STATUS)) {
            throw new Error('Shader link error: ' + gl.getProgramInfoLog(this.program));
        }

        // Get locations
        this.locations = {
            position: gl.getAttribLocation(this.program, 'position'),
            index: gl.getAttribLocation(this.program, 'index'),
            projection: gl.getUniformLocation(this.program, 'projection'),
            view: gl.getUniformLocation(this.program, 'view'),
            modelMatrix: gl.getUniformLocation(this.program, 'modelMatrix'),
            focal: gl.getUniformLocation(this.program, 'focal'),
            viewport: gl.getUniformLocation(this.program, 'viewport'),
            time: gl.getUniformLocation(this.program, 'time'),
            texture: gl.getUniformLocation(this.program, 'u_texture'),
            texWidth: gl.getUniformLocation(this.program, 'texWidth')
        };
        
        
        // Check if locations are valid
        if (this.locations.position === -1) {
            console.error('Failed to get position attribute location!');
        }

        // Create VAO
        this.vao = gl.createVertexArray();
        gl.bindVertexArray(this.vao);

        // Create position buffer (quad vertices for TRIANGLE_FAN)
        const positions = new Float32Array([-2, -2, 2, -2, 2, 2, -2, 2]);
        const posBuffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, posBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, positions, gl.STATIC_DRAW);
        gl.enableVertexAttribArray(this.locations.position);
        gl.vertexAttribPointer(this.locations.position, 2, gl.FLOAT, false, 0, 0);

        // Create index buffer for instanced rendering
        this.indexBuffer = gl.createBuffer();
        
        // Only set up index attribute if it exists in the shader
        if (this.locations.index !== -1) {
            gl.bindBuffer(gl.ARRAY_BUFFER, this.indexBuffer);
            gl.enableVertexAttribArray(this.locations.index);
            gl.vertexAttribIPointer(this.locations.index, 1, gl.INT, false, 0, 0);
            gl.vertexAttribDivisor(this.locations.index, 1); // This is the key - one index per instance
        }

        gl.bindVertexArray(null);

        // GL state
        gl.disable(gl.DEPTH_TEST);
        gl.enable(gl.BLEND);
        // Use special blending mode for gaussian splats (from antimatter15's implementation)
        gl.blendFuncSeparate(gl.ONE_MINUS_DST_ALPHA, gl.ONE, gl.ONE_MINUS_DST_ALPHA, gl.ONE);
        gl.blendEquationSeparate(gl.FUNC_ADD, gl.FUNC_ADD);
        gl.clearColor(0.0, 0.0, 0.0, 0.0); // Transparent background

        // Create worker
        const blob = new Blob([sortWorkerCode], { type: 'application/javascript' });
        this.worker = new Worker(URL.createObjectURL(blob));
        this.worker.onmessage = (e) => {
            this.depthIndex = e.data.depthIndex;
            this.vertexCount = e.data.vertexCount;
        };

        this.initialized = true;
        
        // Initialize gizmo renderer
        this.gizmoRenderer = new GizmoRenderer(gl);
    }

    createShader(type, source) {
        const gl = this.gl;
        const shader = gl.createShader(type);
        gl.shaderSource(shader, source);
        gl.compileShader(shader);
        
        if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
            const shaderType = type === gl.VERTEX_SHADER ? 'vertex' : 'fragment';
            console.error(`${shaderType} shader compile error:`, gl.getShaderInfoLog(shader));
            console.error('First 500 chars of shader source:', source.substring(0, 500));
            gl.deleteShader(shader);
            return null;
        }
        
        return shader;
    }

    async loadFile(url, onProgress) {
        // Skip mock/placeholder URLs
        if (!url || url.includes('mock') || url.includes('placeholder') || url === '#') {
            console.log('Skipping mock/placeholder URL:', url);
            return;
        }
        
        try {
            // Try to load as splatv format first
            const { loadSplatV } = await import('./splatv-reader.js');
            const result = await loadSplatV(url, onProgress);
            
            
            this.splatCount = result.vertexCount;
            this.processSplatvFromReader(result);
        } catch (e) {
            
            // Fallback to simple format
            const response = await fetch(url);
            if (!response.ok) throw new Error('Failed to fetch: ' + response.statusText);
            
            const data = await response.arrayBuffer();
            this.loadSimpleFormat(new Uint8Array(data));
        }
    }

    async loadSplatv(reader, headerData, onProgress) {
        const view = new DataView(headerData.buffer, headerData.byteOffset, headerData.byteLength);
        
        // Skip magic (4 bytes) and version (4 bytes) to get JSON length
        if (headerData.length < 12) {
            throw new Error('Invalid splatv header');
        }
        
        const jsonLength = view.getUint32(8, true);
        
        // Read JSON
        let jsonData = '';
        let bytesRead = headerData.length - 12;
        
        if (bytesRead > 0) {
            jsonData = new TextDecoder().decode(headerData.subarray(12, Math.min(12 + jsonLength, headerData.length)));
        }

        while (jsonData.length < jsonLength) {
            const { done, value } = await reader.read();
            if (done) break;
            const needed = jsonLength - jsonData.length;
            jsonData += new TextDecoder().decode(value.subarray(0, needed));
            bytesRead = needed;
        }

        let metadata;
        try {
            metadata = JSON.parse(jsonData);
        } catch (e) {
            console.error('Failed to parse JSON:', e);
            console.error('JSON data:', jsonData);
            throw new Error('Invalid JSON in splatv file: ' + e.message);
        }
        
        // Handle both array and object formats
        if (Array.isArray(metadata)) {
            // Array format - find the splat data
            const splatData = metadata.find(item => item.type === 'splat');
            if (splatData) {
                // antimatter15 uses 64 bytes per splat (16 uint32 values)
                this.splatCount = splatData.size / 64; // Each splat is 16 * 4 bytes
                this.texWidth = splatData.texwidth;
                this.texHeight = splatData.texheight;
            } else {
                throw new Error('No splat data found in metadata');
            }
        } else {
            // Object format
            this.splatCount = metadata.splatCount || 0;
        }

        // For array format, we need to continue reading to get to the actual splat data
        if (Array.isArray(metadata)) {
            // Skip any remaining JSON data
            let remainingJsonBytes = jsonLength - jsonData.length;
            while (remainingJsonBytes > 0) {
                const { done, value } = await reader.read();
                if (done) break;
                remainingJsonBytes -= value.length;
            }
            
            // Now read the binary splat data
            const chunks = [];
            let totalSize = 0;
            
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                chunks.push(value);
                totalSize += value.length;
                if (onProgress) onProgress(totalSize / (this.splatCount * 32));
            }
            
            // Combine chunks
            const fullData = new Uint8Array(totalSize);
            let offset = 0;
            for (const chunk of chunks) {
                fullData.set(chunk, offset);
                offset += chunk.length;
            }
            
            this.processSplatvData(fullData, metadata);
        } else {
            // Original format handling
            const splatDataSize = this.splatCount * 64; // 16 uint32s per splat
            const splatData = new Uint8Array(splatDataSize);
            let offset = 0;

            while (offset < splatDataSize) {
                const { done, value } = await reader.read();
                if (done) break;
                
                const copySize = Math.min(value.length, splatDataSize - offset);
                splatData.set(value.subarray(0, copySize), offset);
                offset += copySize;
                
                if (onProgress) onProgress(offset / splatDataSize);
            }

            this.processSplatData(splatData, metadata);
        }
    }

    loadSimpleFormat(data) {
        // Ensure proper alignment for Float32Array
        let alignedBuffer;
        if (data.buffer && data.buffer.byteLength % 4 === 0) {
            alignedBuffer = data.buffer;
        } else {
            // Create aligned buffer
            const byteLength = Math.floor(data.byteLength / 4) * 4;
            alignedBuffer = new ArrayBuffer(byteLength);
            new Uint8Array(alignedBuffer).set(new Uint8Array(data.buffer || data, 0, byteLength));
        }
        
        const floatView = new Float32Array(alignedBuffer);
        const vertexStride = 7; // x,y,z,r,g,b,a
        this.splatCount = Math.floor(floatView.length / vertexStride);

        // Convert to texture format (antimatter15 format: 16 uint32s per splat)
        const texWidth = 1024 * 4; // 4 pixels per row unit, 1024 units per row
        const texHeight = Math.ceil(this.splatCount / 1024);
        const textureData = new Uint32Array(texWidth * texHeight * 4);

        const positions = new Float32Array(this.splatCount * 3);

        for (let i = 0; i < this.splatCount; i++) {
            const srcOffset = i * vertexStride;
            const texOffset = i * 16; // 16 uint32s per splat

            // Position
            positions[i * 3] = floatView[srcOffset];
            positions[i * 3 + 1] = floatView[srcOffset + 1];
            positions[i * 3 + 2] = floatView[srcOffset + 2];

            // Store position as floats (using Float32 bit representation)
            const floatArray = new Float32Array([floatView[srcOffset], floatView[srcOffset + 1], floatView[srcOffset + 2]]);
            const uint32View = new Uint32Array(floatArray.buffer);
            textureData[texOffset] = uint32View[0];
            textureData[texOffset + 1] = uint32View[1];
            textureData[texOffset + 2] = uint32View[2];

            // Default rotation quaternion (w,x,y,z) as half floats
            textureData[texOffset + 3] = 0x3c000000; // 1.0, 0.0 as half floats (w,x)
            textureData[texOffset + 4] = 0x00000000; // 0.0, 0.0 as half floats (y,z)
            
            // Default scale as half floats
            textureData[texOffset + 5] = 0x3c003c00; // 1.0, 1.0 as half floats
            textureData[texOffset + 6] = 0x3c000000; // 1.0, 0.0 as half floats

            // Color and opacity (RGBA packed)
            const r = Math.floor(floatView[srcOffset + 3] * 255);
            const g = Math.floor(floatView[srcOffset + 4] * 255);
            const b = Math.floor(floatView[srcOffset + 5] * 255);
            const a = Math.floor(floatView[srcOffset + 6] * 255);
            textureData[texOffset + 7] = (r) | (g << 8) | (b << 16) | (a << 24);
            
            // Fill remaining slots with default motion data (8-15)
            for (let j = 8; j < 16; j++) {
                textureData[texOffset + j] = 0;
            }
        }

        this.uploadTexture(textureData, texWidth, texHeight);
        this.vertexCount = this.splatCount;
        this.worker.postMessage({ positions, vertexCount: this.splatCount });
    }

    processSplatvData(data, metadata) {
        // Process splatv format with texture dimensions from metadata
        const texWidth = this.texWidth || 4096;
        const texHeight = this.texHeight || Math.ceil(this.splatCount / (texWidth / 16)); // 16 = 4 pixels * 4 components
        
        // Ensure proper alignment
        let alignedBuffer;
        if (data.buffer && data.byteOffset === 0 && data.buffer.byteLength % 4 === 0) {
            alignedBuffer = data.buffer;
        } else {
            // Create aligned buffer
            alignedBuffer = new ArrayBuffer(data.byteLength);
            new Uint8Array(alignedBuffer).set(data);
        }
        
        // The data is already in the correct format for texture upload
        const textureData = new Uint32Array(alignedBuffer);
        
        // Extract positions for sorting
        const positions = new Float32Array(this.splatCount * 3);
        
        // Each splat is 64 bytes = 16 uint32s (antimatter15 format)
        for (let i = 0; i < this.splatCount; i++) {
            const offset = i * 16; // 16 uint32s per splat
            // First 3 floats are x,y,z positions stored as uints
            // Use uintBitsToFloat to convert from uint representation
            const view = new DataView(alignedBuffer);
            positions[i * 3] = view.getFloat32(offset * 4, true);
            positions[i * 3 + 1] = view.getFloat32(offset * 4 + 4, true);
            positions[i * 3 + 2] = view.getFloat32(offset * 4 + 8, true);
        }

        this.uploadTexture(textureData, texWidth, texHeight);
        this.worker.postMessage({ positions, vertexCount: this.splatCount });
    }

    processSplatData(data, metadata) {
        // Legacy format handler
        this.processSplatvData(data, metadata);
    }
    
    processSplatvFromReader(result) {
        const { splatData, splatData_buffer, texWidth, texHeight, vertexCount } = result;
        
        // Use the aligned buffer if available, otherwise create one
        let alignedBuffer = splatData_buffer;
        if (!alignedBuffer) {
            // Create a properly aligned buffer
            alignedBuffer = new ArrayBuffer(splatData.length);
            new Uint8Array(alignedBuffer).set(splatData);
        }
        
        // Convert to texture format using the aligned buffer
        const textureData = new Uint32Array(alignedBuffer);
        
        // Create a Float32Array view for position extraction
        const floatView = new Float32Array(alignedBuffer);
        
        // Extract positions for sorting
        const positions = new Float32Array(vertexCount * 3);
        
        // Initialize bounds
        let minX = Infinity, minY = Infinity, minZ = Infinity;
        let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
        
        // The data is already in GPU-ready format
        // Each splat is 64 bytes = 16 floats (antimatter15 format)
        for (let i = 0; i < vertexCount; i++) {
            const floatOffset = i * 16; // 16 floats per splat
            // Positions are stored as the first 3 floats
            const x = floatView[floatOffset];
            const y = floatView[floatOffset + 1];
            const z = floatView[floatOffset + 2];
            
            positions[i * 3] = x;
            positions[i * 3 + 1] = y;
            positions[i * 3 + 2] = z;
            
            // Update bounds
            minX = Math.min(minX, x);
            minY = Math.min(minY, y);
            minZ = Math.min(minZ, z);
            maxX = Math.max(maxX, x);
            maxY = Math.max(maxY, y);
            maxZ = Math.max(maxZ, z);
        }
        
        // Calculate bounds and center
        this.bounds = {
            min: [minX, minY, minZ],
            max: [maxX, maxY, maxZ],
            center: [(minX + maxX) / 2, (minY + maxY) / 2, (minZ + maxZ) / 2],
            size: [maxX - minX, maxY - minY, maxZ - minZ]
        };
        
        console.log('Model bounds:', this.bounds);
        
        this.uploadTexture(textureData, texWidth, texHeight);
        this.splatCount = vertexCount;
        this.vertexCount = vertexCount;
        // Send positions and current model matrix to worker
        this.worker.postMessage({ positions, vertexCount, modelMatrix: this.modelMatrix });
    }

    uploadTexture(data, width, height) {
        const gl = this.gl;

        if (this.texture) gl.deleteTexture(this.texture);

        this.texture = gl.createTexture();
        gl.bindTexture(gl.TEXTURE_2D, this.texture);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA32UI, width, height, 0, gl.RGBA_INTEGER, gl.UNSIGNED_INT, data);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

        // Save texture dimensions
        this.texWidth = width;
        this.texHeight = height;
        this.splatData = data;
    }

    // Direct view matrix setter for camera-controls.js compatibility
    setViewMatrix(viewMatrix) {
        this.viewMatrix.set(viewMatrix);
        this.updateViewProjection();
    }

    // Update projection matrix separately
    updateProjectionMatrix(fov = 45) {
        // Use viewport dimensions (not canvas size) to match 4dgs-stgs_viewer
        const rect = this.canvas.getBoundingClientRect();
        const width = rect.width;
        const height = rect.height;
        const fx = (height / (2 * Math.tan(fov * Math.PI / 360))); // focal length from FOV
        const fy = fx; // assuming square pixels
        const znear = 0.2;
        const zfar = 200;
        
        // Store focal length values
        this.fx = fx;
        this.fy = fy;

        this.projMatrix.set([
            (2 * fx) / width, 0, 0, 0,
            0, -(2 * fy) / height, 0, 0,
            0, 0, zfar / (zfar - znear), 1,
            0, 0, -(zfar * znear) / (zfar - znear), 0
        ]);
    }

    // Matrix multiplication function that matches 4dgs-stgs_viewer
    // Note: This actually computes b × a (not a × b)
    multiply4(a, b) {
        return [
            b[0] * a[0] + b[1] * a[4] + b[2] * a[8] + b[3] * a[12],
            b[0] * a[1] + b[1] * a[5] + b[2] * a[9] + b[3] * a[13],
            b[0] * a[2] + b[1] * a[6] + b[2] * a[10] + b[3] * a[14],
            b[0] * a[3] + b[1] * a[7] + b[2] * a[11] + b[3] * a[15],
            b[4] * a[0] + b[5] * a[4] + b[6] * a[8] + b[7] * a[12],
            b[4] * a[1] + b[5] * a[5] + b[6] * a[9] + b[7] * a[13],
            b[4] * a[2] + b[5] * a[6] + b[6] * a[10] + b[7] * a[14],
            b[4] * a[3] + b[5] * a[7] + b[6] * a[11] + b[7] * a[15],
            b[8] * a[0] + b[9] * a[4] + b[10] * a[8] + b[11] * a[12],
            b[8] * a[1] + b[9] * a[5] + b[10] * a[9] + b[11] * a[13],
            b[8] * a[2] + b[9] * a[6] + b[10] * a[10] + b[11] * a[14],
            b[8] * a[3] + b[9] * a[7] + b[10] * a[11] + b[11] * a[15],
            b[12] * a[0] + b[13] * a[4] + b[14] * a[8] + b[15] * a[12],
            b[12] * a[1] + b[13] * a[5] + b[14] * a[9] + b[15] * a[13],
            b[12] * a[2] + b[13] * a[6] + b[14] * a[10] + b[15] * a[14],
            b[12] * a[3] + b[13] * a[7] + b[14] * a[11] + b[15] * a[15]
        ];
    }

    updateViewProjection() {
        // Trigger sorting
        const now = performance.now();
        if (now - this.lastSortTime > 16 && this.worker && this.vertexCount > 0) {
            this.lastSortTime = now;
            // Use multiply4 to match 4dgs-stgs_viewer: multiply4(proj, view) = view × proj
            const viewProj = new Float32Array(this.multiply4(this.projMatrix, this.viewMatrix));
            // Send model matrix along with viewProj
            this.worker.postMessage({ viewProj, modelMatrix: this.modelMatrix });
        }
    }

    // Legacy setCamera for compatibility - converts to view matrix
    setCamera(position, rotation, fov = 45) {
        // Update view matrix
        const [x, y, z] = position;
        const [rx, ry, rz] = rotation;

        const cx = Math.cos(rx), sx = Math.sin(rx);
        const cy = Math.cos(ry), sy = Math.sin(ry);
        const cz = Math.cos(rz), sz = Math.sin(rz);

        // Rotation matrix
        const r00 = cy * cz;
        const r01 = sx * sy * cz - cx * sz;
        const r02 = cx * sy * cz + sx * sz;
        const r10 = cy * sz;
        const r11 = sx * sy * sz + cx * cz;
        const r12 = cx * sy * sz - sx * cz;
        const r20 = -sy;
        const r21 = sx * cy;
        const r22 = cx * cy;

        // View matrix - using row-major layout like 4dgs-stgs_viewer
        this.viewMatrix.set([
            r00, r01, r02, 0,
            r10, r11, r12, 0,
            r20, r21, r22, 0,
            -(r00 * x + r10 * y + r20 * z),
            -(r01 * x + r11 * y + r21 * z),
            -(r02 * x + r12 * y + r22 * z),
            1
        ]);

        // Update projection matrix
        this.updateProjectionMatrix(fov);
        
        // Update view projection
        this.updateViewProjection();
    }

    render() {
        if (!this.initialized || !this.texture || !this.depthIndex || this.vertexCount === 0) {
            return;
        }

        const gl = this.gl;
        
        
        // Clear to transparent - preserve alpha for AR overlay
        gl.clearColor(0.0, 0.0, 0.0, 0.0);
        gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
        
        gl.useProgram(this.program);

        // Set uniforms
        gl.uniformMatrix4fv(this.locations.projection, false, this.projMatrix);
        gl.uniformMatrix4fv(this.locations.view, false, this.viewMatrix);
        gl.uniformMatrix4fv(this.locations.modelMatrix, false, this.modelMatrix);
        
        // Use calculated focal length from projection matrix
        // If not initialized yet, use default values
        const fx = this.fx || (this.canvas.height / (2 * Math.tan(45 * Math.PI / 360)));
        const fy = this.fy || fx;
        gl.uniform2f(this.locations.focal, fx, fy);
        // Use actual viewport dimensions (not canvas size) to match 4dgs-stgs_viewer
        // This ensures proper splat sizing independent of canvas resolution
        const rect = this.canvas.getBoundingClientRect();
        gl.uniform2f(this.locations.viewport, rect.width, rect.height);
        // gl.uniform1f(this.locations.time, Math.sin(Date.now() / 1000) / 2 + 1 / 2);

        // Ping-Pong
        const duration = 3000; // 單程時間（毫秒），可自行調整
        const cycle = (Date.now() % (duration * 2)) / duration;
        const t = cycle > 1 ? 2 - cycle : cycle;
        gl.uniform1f(this.locations.time, t);

        gl.uniform1f(this.locations.texWidth, this.texWidth || 4096);

        // Bind texture
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, this.texture);
        gl.uniform1i(this.locations.texture, 0);

        // Draw using instanced rendering
        gl.bindVertexArray(this.vao);
        
        // Update index buffer with sorted indices (only if index attribute exists)
        if (this.locations.index !== -1 && this.depthIndex) {
            gl.bindBuffer(gl.ARRAY_BUFFER, this.indexBuffer);
            gl.bufferData(gl.ARRAY_BUFFER, this.depthIndex, gl.DYNAMIC_DRAW);
        }
        
        // Check for GL errors before draw
        const errorBefore = gl.getError();
        if (errorBefore !== gl.NO_ERROR) {
            console.error('GL Error before draw:', errorBefore);
        }
        
        // Draw all splats
        const drawCount = this.vertexCount;
        
        
        
        // Draw instances with limit
        try {
            if (drawCount > 0) {
                gl.drawArraysInstanced(gl.TRIANGLE_FAN, 0, 4, drawCount);
            }
        } catch (e) {
            console.error('Draw error:', e);
        }
        
        // Check for GL errors after draw
        const errorAfter = gl.getError();
        if (errorAfter !== gl.NO_ERROR) {
            console.error('GL Error after draw:', errorAfter);
        }
        
        
        gl.bindVertexArray(null);
        
        // Render gizmo if enabled
        if (this.showGizmo && this.gizmoRenderer) {
            this.gizmoRenderer.render(this.projMatrix, this.viewMatrix, this.modelMatrix);
        }
    }

    resize(width, height) {
        this.canvas.width = width;
        this.canvas.height = height;
        this.gl.viewport(0, 0, width, height);
    }

    dispose() {
        const gl = this.gl;
        
        if (this.texture) gl.deleteTexture(this.texture);
        if (this.vao) gl.deleteVertexArray(this.vao);
        if (this.program) gl.deleteProgram(this.program);
        if (this.worker) this.worker.terminate();
        if (this.gizmoRenderer) this.gizmoRenderer.dispose();
    }
    
    getBounds() {
        return this.bounds;
    }
    
    setModelTransform(transform) {
        if (!transform) {
            // Reset to identity matrix
            this.modelMatrix.set([
                1, 0, 0, 0,
                0, 1, 0, 0,
                0, 0, 1, 0,
                0, 0, 0, 1
            ]);
        } else {
            const { position, rotation, scale } = transform;
            
            // Create translation matrix
            const tx = position?.x || 0;
            const ty = position?.y || 0;
            const tz = position?.z || 0;
            
            // Create rotation matrices
            const rx = rotation?.rx || 0;
            const ry = rotation?.ry || 0;
            const rz = rotation?.rz || 0;
            
            const cx = Math.cos(rx), sx = Math.sin(rx);
            const cy = Math.cos(ry), sy = Math.sin(ry);
            const cz = Math.cos(rz), sz = Math.sin(rz);
            
            // Create scale values
            const scaleX = scale?.x || 1;
            const scaleY = scale?.y || 1;
            const scaleZ = scale?.z || 1;
            
            // Combine rotation matrices (R = Rz * Ry * Rx)
            // First compute Rx
            const rx_00 = 1, rx_01 = 0, rx_02 = 0;
            const rx_10 = 0, rx_11 = cx, rx_12 = -sx;
            const rx_20 = 0, rx_21 = sx, rx_22 = cx;
            
            // Then compute Ry
            const ry_00 = cy, ry_01 = 0, ry_02 = sy;
            const ry_10 = 0, ry_11 = 1, ry_12 = 0;
            const ry_20 = -sy, ry_21 = 0, ry_22 = cy;
            
            // Then compute Rz
            const rz_00 = cz, rz_01 = -sz, rz_02 = 0;
            const rz_10 = sz, rz_11 = cz, rz_12 = 0;
            const rz_20 = 0, rz_21 = 0, rz_22 = 1;
            
            // Compute Ry * Rx
            const ryx_00 = ry_00 * rx_00 + ry_01 * rx_10 + ry_02 * rx_20;
            const ryx_01 = ry_00 * rx_01 + ry_01 * rx_11 + ry_02 * rx_21;
            const ryx_02 = ry_00 * rx_02 + ry_01 * rx_12 + ry_02 * rx_22;
            const ryx_10 = ry_10 * rx_00 + ry_11 * rx_10 + ry_12 * rx_20;
            const ryx_11 = ry_10 * rx_01 + ry_11 * rx_11 + ry_12 * rx_21;
            const ryx_12 = ry_10 * rx_02 + ry_11 * rx_12 + ry_12 * rx_22;
            const ryx_20 = ry_20 * rx_00 + ry_21 * rx_10 + ry_22 * rx_20;
            const ryx_21 = ry_20 * rx_01 + ry_21 * rx_11 + ry_22 * rx_21;
            const ryx_22 = ry_20 * rx_02 + ry_21 * rx_12 + ry_22 * rx_22;
            
            // Compute Rz * (Ry * Rx)
            const r00 = rz_00 * ryx_00 + rz_01 * ryx_10 + rz_02 * ryx_20;
            const r01 = rz_00 * ryx_01 + rz_01 * ryx_11 + rz_02 * ryx_21;
            const r02 = rz_00 * ryx_02 + rz_01 * ryx_12 + rz_02 * ryx_22;
            const r10 = rz_10 * ryx_00 + rz_11 * ryx_10 + rz_12 * ryx_20;
            const r11 = rz_10 * ryx_01 + rz_11 * ryx_11 + rz_12 * ryx_21;
            const r12 = rz_10 * ryx_02 + rz_11 * ryx_12 + rz_12 * ryx_22;
            const r20 = rz_20 * ryx_00 + rz_21 * ryx_10 + rz_22 * ryx_20;
            const r21 = rz_20 * ryx_01 + rz_21 * ryx_11 + rz_22 * ryx_21;
            const r22 = rz_20 * ryx_02 + rz_21 * ryx_12 + rz_22 * ryx_22;
            
            // Apply scale to rotation matrix
            const m00 = r00 * scaleX;
            const m01 = r01 * scaleY;
            const m02 = r02 * scaleZ;
            const m10 = r10 * scaleX;
            const m11 = r11 * scaleY;
            const m12 = r12 * scaleZ;
            const m20 = r20 * scaleX;
            const m21 = r21 * scaleY;
            const m22 = r22 * scaleZ;
            
            // Set model matrix (column-major order for WebGL)
            this.modelMatrix.set([
                m00, m10, m20, 0,
                m01, m11, m21, 0,
                m02, m12, m22, 0,
                tx, ty, tz, 1
            ]);
        }
        
        // Send updated model matrix to worker and trigger re-sort
        if (this.worker) {
            this.worker.postMessage({ modelMatrix: this.modelMatrix });
            // Force immediate re-sort
            this.lastSortTime = 0;
        }
    }
    
    // Gizmo control methods
    setGizmoVisible(visible) {
        this.showGizmo = visible;
    }
    
    setGizmoMode(mode) {
        this.gizmoMode = mode;
        if (this.gizmoRenderer) {
            this.gizmoRenderer.setMode(mode);
        }
    }
    
    setGizmoPosition(position) {
        if (this.gizmoRenderer) {
            this.gizmoRenderer.setPosition([position.x || 0, position.y || 0, position.z || 0]);
        }
    }
    
    setGizmoScale(scale) {
        if (this.gizmoRenderer) {
            this.gizmoRenderer.setScale(scale);
        }
    }
    
    intersectGizmo(mouseX, mouseY) {
        if (!this.gizmoRenderer || !this.showGizmo) return null;
        
        // Convert mouse coordinates to NDC
        const rect = this.canvas.getBoundingClientRect();
        const x = (mouseX - rect.left) / rect.width * 2 - 1;
        const y = -(mouseY - rect.top) / rect.height * 2 + 1;
        
        // TODO: Calculate ray from camera
        const rayOrigin = [mouseX, mouseY];
        const rayDirection = [0, 0, -1]; // Placeholder
        
        return this.gizmoRenderer.intersectRay(
            rayOrigin,
            rayDirection,
            this.viewMatrix,
            this.projMatrix,
            [0, 0, rect.width, rect.height]
        );
    }
}